import React, { useState, useMemo } from 'react'
import PropTypes from 'prop-types'
import { Button, TextInput, TextArea, Switcher, Selector } from 'shared/components'
import './styles.scss'

const MetadataFilter = ({ onApplyFilter, onClear }) => {
  const [filters, setFilters] = useState([
    { path: '', type: 'equals', value: '', index: '', valueType: 'string' }
  ])
  const [advancedMode, setAdvancedMode] = useState(false)
  const [advancedQuery, setAdvancedQuery] = useState('')

  const handleFilterChange = (i, field, value) => {
    const updated = [...filters]
    updated[i][field] = value
    setFilters(updated)
  }

  const addFilter = () => {
    setFilters([
      ...filters,
      { path: '', type: 'equals', value: '', index: '', valueType: 'string' }
    ])
  }

  const removeFilter = i => {
    setFilters(filters.filter((_, index) => index !== i))
  }

  const formatPathToQueryParam = (path, type, index) => {
    if (type === 'array-item') {
      return `metadata__${path}__${index}`
    }
    if (type === 'array-contains') {
      return `metadata__${path.split('.').join('__')}__contains`
    }
    return `metadata__${path.split('.').join('__')}`
  }

  const previewQuery = useMemo(() => {
    if (advancedMode) return advancedQuery

    return filters
      .map(({ path, value, type, index, valueType }) => {
        if (!path || !value) return ''

        const key = formatPathToQueryParam(path, type, index)
        let finalValue = value

        if (valueType === 'string') {
          // For string type, wrap the value in quotes
          return `${key}=%22${value}%22`
        } else if (valueType === 'object' || valueType === 'array') {
          try {
            finalValue = JSON.stringify(JSON.parse(value))
          } catch (e) {
            finalValue = value
          }
        } else if (valueType === 'boolean') {
          finalValue = value.toLowerCase() === 'true'
        } else if (valueType === 'null') {
          finalValue = 'null'
        }

        // For non-string types, encode the value
        return `${key}=${encodeURIComponent(finalValue)}`
      })
      .filter(Boolean)
      .join('&')
  }, [filters, advancedQuery, advancedMode])

  const handleModeToggle = newAdvancedMode => {
    if (newAdvancedMode && !advancedMode) {
      // Switching from simple to advanced - convert filters to query string
      setAdvancedQuery(previewQuery)
    } else if (!newAdvancedMode && advancedMode) {
      // Switching from advanced to simple - try to parse query string back to filters
      try {
        const params = new URLSearchParams(advancedQuery)
        const newFilters = []

        for (const [key, value] of params.entries()) {
          if (key.startsWith('metadata__')) {
            const pathParts = key.replace('metadata__', '').split('__')
            let type = 'equals'
            let index = ''
            let path = pathParts.join('.')

            // Check for special cases
            if (pathParts[pathParts.length - 1] === 'contains') {
              type = 'array-contains'
              path = pathParts.slice(0, -1).join('.')
            } else if (!isNaN(pathParts[pathParts.length - 1])) {
              type = 'array-item'
              index = pathParts[pathParts.length - 1]
              path = pathParts.slice(0, -1).join('.')
            }

            // Determine value type and clean value
            let cleanValue = decodeURIComponent(value)
            let valueType = 'string'

            // Remove quotes if present (string values)
            if (cleanValue.startsWith('"') && cleanValue.endsWith('"')) {
              cleanValue = cleanValue.slice(1, -1)
              valueType = 'string'
            } else if (cleanValue === 'true' || cleanValue === 'false') {
              valueType = 'boolean'
            } else if (cleanValue === 'null') {
              valueType = 'null'
            } else if (!isNaN(cleanValue) && cleanValue !== '') {
              valueType = 'number'
            } else if (cleanValue.startsWith('{') || cleanValue.startsWith('[')) {
              try {
                JSON.parse(cleanValue)
                valueType = cleanValue.startsWith('{') ? 'object' : 'array'
              } catch (e) {
                valueType = 'string'
              }
            }

            newFilters.push({
              path,
              type,
              value: cleanValue,
              index,
              valueType
            })
          }
        }

        if (newFilters.length > 0) {
          setFilters(newFilters)
        }
      } catch (e) {
        // If parsing fails, keep current filters
        // eslint-disable-next-line no-console
        console.warn('Failed to parse advanced query back to simple filters:', e)
      }
    }

    setAdvancedMode(newAdvancedMode)
  }

  const handleApplyFilter = () => {
    if (onApplyFilter) {
      onApplyFilter(previewQuery, advancedMode)
    }
  }

  const handleClear = () => {
    setFilters([{ path: '', type: 'equals', value: '', index: '', valueType: 'string' }])
    setAdvancedQuery('')
    if (onClear) {
      onClear()
    }
  }

  const handleKeyDown = e => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      handleApplyFilter()
    }
  }

  const filterTypeOptions = [
    { label: __('equals'), value: 'equals' },
    { label: __('list item equals'), value: 'array-item' },
    { label: __('list contains'), value: 'array-contains' }
  ]

  const valueTypeOptions = [
    { label: __('string'), value: 'string' },
    { label: __('number'), value: 'number' },
    { label: __('boolean'), value: 'boolean' },
    { label: __('null'), value: 'null' },
    { label: __('object'), value: 'object' },
    { label: __('array'), value: 'array' }
  ]

  return (
    <div className='MetadataFilter' onKeyDown={handleKeyDown}>
      <div className='MetadataFilter-header'>
        <h3>{__('Metadata Filter')}</h3>
        <div className='MetadataFilter-mode-toggle'>
          <span>{__('Advanced Mode')}</span>
          <Switcher size='sm' handleChange={handleModeToggle} checked={advancedMode} />
        </div>
      </div>

      <div className='MetadataFilter-content'>
        {!advancedMode ? (
          <div className='MetadataFilter-simple'>
            {filters.map((filter, index) => (
              <div key={index} className='MetadataFilter-row'>
                <div className='MetadataFilter-field'>
                  <TextInput
                    placeholder={__('Path (e.g. customer.region)')}
                    value={filter.path}
                    handleChange={val => handleFilterChange(index, 'path', val)}
                  />
                </div>
                <div className='MetadataFilter-field'>
                  <Selector
                    options={filterTypeOptions}
                    value={filter.type}
                    handleChange={val => handleFilterChange(index, 'type', val)}
                  />
                </div>
                {filter.type === 'array-item' && (
                  <div className='MetadataFilter-field'>
                    <TextInput
                      placeholder={__('Index (e.g. 0)')}
                      value={filter.index}
                      handleChange={val => handleFilterChange(index, 'index', val)}
                    />
                  </div>
                )}
                <div className='MetadataFilter-field'>
                  <TextInput
                    placeholder={__('Value')}
                    value={filter.value}
                    handleChange={val => handleFilterChange(index, 'value', val)}
                  />
                </div>
                <div className='MetadataFilter-field'>
                  <Selector
                    options={valueTypeOptions}
                    value={filter.valueType}
                    handleChange={val => handleFilterChange(index, 'valueType', val)}
                  />
                </div>
                <div className='MetadataFilter-field MetadataFilter-actions'>
                  <Button theme='error' onClick={() => removeFilter(index)}>
                    ✕
                  </Button>
                </div>
              </div>
            ))}
            <Button theme='info' onClick={addFilter} className='MetadataFilter-add-filter-btn'>
              {__('+ Add Filter')}
            </Button>
          </div>
        ) : (
          <div className='MetadataFilter-advanced'>
            <p className='MetadataFilter-help-text'>
              {__('Enter metadata filters as a raw query string, using metadata__-formatted keys.')}
            </p>
            <TextArea
              value={advancedQuery}
              handleChange={setAdvancedQuery}
              placeholder={`metadata__customer__region="Europe"&metadata__tags__contains=beta&metadata__devices__0__type="usb"`}
              rows='6'
            />
          </div>
        )}
      </div>

      <div className='MetadataFilter-preview'>
        <p>
          <strong>{__('Preview')}:</strong> <code>{previewQuery}</code>
        </p>
      </div>

      <div className='MetadataFilter-footer'>
        <Button theme='default' onClick={handleClear}>
          {__('Clear')}
        </Button>
        <Button theme='info' onClick={handleApplyFilter}>
          {__('Apply Filters')}
        </Button>
      </div>
    </div>
  )
}

MetadataFilter.propTypes = {
  onApplyFilter: PropTypes.func,
  onClear: PropTypes.func
}

MetadataFilter.defaultProps = {
  onApplyFilter: () => {},
  onClear: () => {}
}

export default MetadataFilter
